import { lazy, Suspense } from 'react';
import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router';
import { App } from './app';
import { IsolatedFrameApp } from './IsolatedFrameApp';

// Lazy so the styleguide gallery (its CSS + inlined mirror HTML + vanilla lucide)
// never enters the main chunk — it ships as its own code-split bundle.
const StyleguidePage = lazy(() => import('./styleguide/StyleguidePage'));

/**
 * Real path-based routes (TanStack Router):
 *   /                  — canvas editor; no project open → projects screen
 *   /p/<projectId>     — canvas editor with that project loaded (reload-safe)
 *   /frame/<frameId>   — isolated single-frame preview tab
 *   /styleguide        — living design reference (Phase A compatibility gate)
 *
 * Deep links resolve on the static host via the CloudFront 403/404 → index.html
 * rewrite (infra/lib/frontend-stack.ts) and Vite's default SPA dev fallback.
 */

const rootRoute = createRootRoute({ component: () => <Outlet /> });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: () => <App />,
});

const projectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/p/$projectId',
  component: function ProjectRoute() {
    const { projectId } = projectRoute.useParams();
    return <App routeProjectId={projectId} />;
  },
});

const frameRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/frame/$frameId',
  component: function FrameRoute() {
    const { frameId } = frameRoute.useParams();
    return <IsolatedFrameApp frameId={frameId} />;
  },
});

const styleguideRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/styleguide',
  component: function StyleguideRoute() {
    return (
      <Suspense fallback={null}>
        <StyleguidePage />
      </Suspense>
    );
  },
});

const routeTree = rootRoute.addChildren([indexRoute, projectRoute, frameRoute, styleguideRoute]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
